import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Briefcase, MapPin, Building2, DollarSign, TrendingUp, Calendar, FileText,
  MessageCircle, CheckCircle2, XCircle, HelpCircle, Loader2, Send, Paperclip,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtUSD = (n) =>
  n == null || n === "" || Number.isNaN(Number(n))
    ? "—"
    : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtPct = (n) => (n == null || n === "" ? "—" : `${Number(n).toFixed(2)}%`);

/** Render ISO YYYY-MM-DD as MM/DD/YYYY for US-format display on the
 *  public investor view. Falls back to the original string if parsing
 *  fails (so we never silently lose the value). */
const fmtUSDate = (s) => {
  if (!s) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  return s;
};

/**
 * Public deal review page. URL: /deal/view/:token
 * No auth required. Investors:
 *   - See full deal payload (financials, sponsor, attachments)
 *   - Soft-circle: Interested / Pass / Need More Info
 *   - Commit a $ amount with typed e-signature
 *   - Post Q&A comments
 */
export default function PublicDeal() {
  const { token } = useParams();
  const [state, setState] = useState({ kind: "loading" });
  const [tab, setTab] = useState("overview");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/public/deals/${token}`);
      document.title = `${data.deal.name} · Investment Opportunity`;
      setState({ kind: "ok", data });
    } catch (e) {
      const code = e?.response?.status;
      if (code === 404) return setState({ kind: "missing" });
      setState({ kind: "error", message: e?.response?.data?.detail || "Could not load deal" });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  if (state.kind === "loading") {
    return <Centered><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></Centered>;
  }
  if (state.kind === "missing") {
    return <Centered>
      <div className="text-center">
        <Briefcase className="w-10 h-10 mx-auto text-slate-300" />
        <h2 className="mt-3 text-lg font-semibold text-slate-700">Deal not found</h2>
        <p className="text-sm text-slate-500 mt-1">This invitation link may have been revoked.</p>
      </div>
    </Centered>;
  }
  if (state.kind === "error") {
    return <Centered>
      <div className="text-center text-rose-600">{String(state.message)}</div>
    </Centered>;
  }

  const { deal, invitation, sponsor, comments } = state.data;
  const sponsorName = sponsor.company || sponsor.full_name || "the sponsor";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <div className="relative h-[320px] md:h-[420px] bg-slate-900 overflow-hidden" data-testid="public-deal-hero">
        <img src={`${API}/public/deals/${token}/image?size=1600x600`} alt={deal.name}
          className="absolute inset-0 w-full h-full object-cover opacity-80"
          onError={(e)=>{ e.currentTarget.style.display = "none"; }} />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 pt-8 pb-16 text-white">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-white/80">
              <Briefcase className="w-3.5 h-3.5" /> Investment Opportunity · {sponsorName}
            </div>
            <h1 className="mt-2 text-3xl md:text-5xl font-semibold tracking-tight">{deal.name}</h1>
            <div className="mt-2 flex items-center gap-1.5 text-white/85 text-sm">
              <MapPin className="w-4 h-4" /> {deal.formatted_address || deal.address}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-6 -mt-10 pb-16 relative z-10">
        {/* KPI Strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-white rounded-sm shadow-lg border border-slate-200 p-4 md:p-5" data-testid="public-deal-kpis">
          <Kpi icon={DollarSign} label="Purchase Price" value={fmtUSD(deal.purchase_price)} />
          <Kpi icon={TrendingUp} label="Cap Rate" value={fmtPct(deal.cap_rate)} accent="emerald" />
          <Kpi icon={TrendingUp} label="Projected IRR" value={fmtPct(deal.projected_irr)} accent="indigo" />
          <Kpi icon={DollarSign} label="Equity Required" value={fmtUSD(deal.equity_required)} accent="amber" />
        </div>

        {/* Tabs */}
        <div className="mt-6 flex items-center gap-1 border-b border-slate-200" data-testid="public-deal-tabs">
          {["overview", "financials", "documents", "qa"].map(t => (
            <button key={t} onClick={()=>setTab(t)}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border-b-2 -mb-px transition-colors
                ${tab === t ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}
              data-testid={`tab-${t}`}>
              {t === "qa" ? "Q&A" : t}
            </button>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-5">
            {tab === "overview" && <OverviewPane deal={deal} token={token} />}
            {tab === "financials" && <FinancialsPane deal={deal} />}
            {tab === "documents" && <DocumentsPane deal={deal} token={token} />}
            {tab === "qa" && <QAPane invitation={invitation} comments={comments} token={token} onChanged={load} />}
          </div>

          {/* Sticky response card */}
          <div className="lg:col-span-1">
            <ResponseCard invitation={invitation} deal={deal} token={token} onChanged={load} />
          </div>
        </div>
      </div>

      <footer className="border-t border-slate-200 bg-white py-6 text-center text-xs font-mono uppercase tracking-widest text-slate-400">
        Confidential — Distributed by {sponsorName} · Powered by RealtyVault
      </footer>
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">{children}</div>;
}

function Kpi({ icon: Icon, label, value, accent }) {
  const color = {
    emerald: "text-emerald-700",
    indigo: "text-indigo-700",
    amber: "text-amber-700",
  }[accent] || "text-slate-800";
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-slate-500">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`mt-1 text-xl font-semibold font-mono ${color}`}>{value}</div>
    </div>
  );
}

function OverviewPane({ deal, token }) {
  const photos = deal.photos || [];
  return (
    <>
      {photos.length > 0 && (
        <Card title={`Property Photos (${photos.length})`}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {photos.map((p) => (
              <a key={p.id} href={`${API}/public/deals/${token}/photos/${p.id}`}
                target="_blank" rel="noreferrer"
                className="block aspect-[4/3] rounded-sm overflow-hidden border border-slate-200 hover:opacity-90 transition-opacity">
                <img src={`${API}/public/deals/${token}/photos/${p.id}`}
                  alt={p.name} className="w-full h-full object-cover" />
              </a>
            ))}
          </div>
        </Card>
      )}
      <Card title="Executive Summary">
        {deal.executive_summary ? (
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{deal.executive_summary}</p>
        ) : (
          <p className="text-sm text-slate-400">No summary provided.</p>
        )}
      </Card>
      <Card title="Property Snapshot">
        <dl className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
          <DLRow label="Transaction" value={deal.transaction_type === "refinance" ? "Refinance" : "Purchase"} />
          <DLRow label={deal.transaction_type === "refinance" ? "Refi Date" : "Purchase Date"}
                 value={fmtUSDate(deal.purchase_date)} />
          <DLRow label="Type" value={(deal.property_type || "—").replace("_", " ")} />
          <DLRow label="Sqft" value={deal.sqft ? Number(deal.sqft).toLocaleString() : "—"} />
          <DLRow label="Units" value={deal.units ?? "—"} />
          <DLRow label="Year Built" value={deal.year_built ?? "—"} />
          <DLRow label="Hold Period" value={deal.hold_period_years ? `${deal.hold_period_years} yrs` : "—"} />
          <DLRow label="Target Close" value={fmtUSDate(deal.target_close_date)} />
        </dl>
      </Card>
      <Card title="Sponsor">
        <div className="text-sm">
          <div className="font-semibold text-slate-900">{deal.sponsor_name || "—"}</div>
          {deal.sponsor_track_record && (
            <div className="text-slate-600 mt-1">{deal.sponsor_track_record}</div>
          )}
        </div>
      </Card>
    </>
  );
}

function FinancialsPane({ deal }) {
  return (
    <>
      <Card title="Returns &amp; Equity">
        <dl className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
          <DLRow label={deal.transaction_type === "refinance" ? "Current Value" : "Purchase Price"}
                 value={fmtUSD(deal.purchase_price)} />
          <DLRow label="Existing Liens" value={fmtUSD(deal.existing_liens)} />
          <DLRow label="As-Is Value" value={fmtUSD(deal.as_is_value)} />
          <DLRow label="Improvements" value={fmtUSD(deal.improvements)} />
          <DLRow label="Expected ARV" value={fmtUSD(deal.arv_value)} />
          <DLRow label="Tax-Assessed Value" value={fmtUSD(deal.tax_value)} />
          <DLRow label="All-In Basis" value={(() => {
            const p = Number(deal.purchase_price) || 0;
            const i = Number(deal.improvements) || 0;
            return p + i > 0 ? fmtUSD(p + i) : "—";
          })()} />
          <DLRow label="NOI (Year 1)" value={fmtUSD(deal.noi)} />
          <DLRow label="Cap Rate" value={fmtPct(deal.cap_rate)} />
          <DLRow label="Projected IRR" value={fmtPct(deal.projected_irr)} />
          <DLRow label="Equity Required" value={fmtUSD(deal.equity_required)} />
          <DLRow label="Minimum Investment" value={fmtUSD(deal.minimum_investment)} />
        </dl>
      </Card>
      <Card title="Financing">
        <dl className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
          <DLRow label="Loan Amount" value={fmtUSD(deal.loan_amount)} />
          <DLRow label="LTV" value={fmtPct(deal.ltv)} />
          <DLRow label="Interest Rate" value={fmtPct(deal.interest_rate)} />
          <DLRow label="Loan Type" value={deal.loan_type === "interest_only" ? "Interest Only" : "Amortizing"} />
          <DLRow label={deal.loan_type === "interest_only" ? "Term" : "Amortization"}
                 value={deal.amortization_years ? `${deal.amortization_years} yrs` : "—"} />
          <DLRow label="Monthly Payment" value={(() => {
            const P = Number(deal.loan_amount);
            const r = Number(deal.interest_rate) / 100 / 12;
            const n = Math.round(Number(deal.amortization_years || 0) * 12);
            if (!P || !r || !n) return "—";
            const pmt = deal.loan_type === "interest_only"
              ? P * r
              : (P * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1);
            return `$${pmt.toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo`;
          })()} />
        </dl>
        {deal.loan_notes && (
          <p className="mt-4 text-sm text-slate-600 leading-relaxed whitespace-pre-wrap border-t border-slate-100 pt-3">{deal.loan_notes}</p>
        )}
      </Card>
    </>
  );
}

function DocumentsPane({ deal, token }) {
  const atts = deal.attachments || [];
  return (
    <Card title="Due Diligence Documents" icon={FileText}>
      {atts.length === 0 ? (
        <p className="text-sm text-slate-400">No documents attached to this deal yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded-sm">
          {atts.map(a => (
            <li key={a.id} className="px-4 py-3 flex items-center gap-3">
              <Paperclip className="w-4 h-4 text-slate-400" />
              <span className="flex-1 text-sm text-slate-800 truncate">{a.name}</span>
              <span className="text-xs font-mono text-slate-400">{(a.size / 1024).toFixed(0)} KB</span>
              <a href={`${API}/public/deals/${token}/attachments/${a.id}`} target="_blank" rel="noreferrer"
                className="text-xs font-mono uppercase tracking-widest text-slate-700 hover:text-slate-900 px-3 py-1.5 rounded-sm bg-slate-100 hover:bg-slate-200">
                View
              </a>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function QAPane({ invitation, comments, token, onChanged }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/public/deals/${token}/comments`, {
        body: body.trim(),
        author_name: invitation.name || invitation.email,
      });
      setBody("");
      toast.success("Question sent");
      onChanged();
    } catch (e) {
      toast.error("Could not post — try again");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title="Questions & Answers" icon={MessageCircle}>
      {comments.length === 0 ? (
        <p className="text-sm text-slate-400 mb-4">No questions yet — be the first to ask.</p>
      ) : (
        <ul className="space-y-3 mb-4">
          {comments.map(c => (
            <li key={c.id} className={`rounded-sm border p-3 text-sm
              ${c.author_role === "sponsor" ? "bg-slate-50 border-slate-200" : "bg-indigo-50/40 border-indigo-100"}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                  {c.author_role === "sponsor" ? "Sponsor" : c.author_name}
                </span>
                <span className="text-[10px] font-mono text-slate-400">{new Date(c.created_at).toLocaleString()}</span>
              </div>
              <div className="text-slate-700 whitespace-pre-wrap">{c.body}</div>
            </li>
          ))}
        </ul>
      )}
      <textarea rows={3} value={body} onChange={(e)=>setBody(e.target.value)}
        placeholder="Ask the sponsor a question…"
        data-testid="public-qa-input"
        className="w-full rounded-sm border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300" />
      <div className="flex justify-end mt-2">
        <button onClick={submit} disabled={busy || !body.trim()}
          data-testid="public-qa-submit"
          className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white px-4 py-2 rounded-sm text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5">
          <Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Submit Question"}
        </button>
      </div>
    </Card>
  );
}

function ResponseCard({ invitation, deal, token, onChanged }) {
  const responded = !!invitation.responded_at;
  const [mode, setMode] = useState(invitation.response_status || null);
  const [amount, setAmount] = useState(invitation.response_amount || "");
  const [esigName, setEsigName] = useState(invitation.esig_name || invitation.name || "");
  const [note, setNote] = useState(invitation.response_note || "");
  const [busy, setBusy] = useState(false);

  const submit = async (status) => {
    if (status === "committed") {
      if (!amount || Number(amount) <= 0) { toast.error("Enter a commit amount"); return; }
      if (!esigName.trim() || esigName.trim().length < 2) { toast.error("Type your full legal name to e-sign"); return; }
    }
    setBusy(true);
    try {
      await axios.post(`${API}/public/deals/${token}/response`, {
        status,
        amount: status === "committed" ? Number(amount) : null,
        esig_name: status === "committed" ? esigName.trim() : null,
        esig_date: status === "committed" ? new Date().toISOString() : null,
        note: note.trim() || null,
      });
      toast.success("Response recorded — thank you!");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit — try again");
    } finally {
      setBusy(false);
    }
  };

  if (responded && invitation.response_status === "committed") {
    return (
      <div className="rounded-sm border border-indigo-200 bg-indigo-50 p-5 sticky top-6" data-testid="public-deal-committed">
        <ShieldCheck className="w-7 h-7 text-indigo-700" />
        <h3 className="mt-2 font-semibold text-indigo-900">Commitment Recorded</h3>
        <p className="text-sm text-indigo-800 mt-1">${Number(invitation.response_amount || 0).toLocaleString()} signed by <span className="font-semibold">{invitation.esig_name}</span>.</p>
        <p className="text-xs text-indigo-700 mt-2">The sponsor has been notified and will reach out with subscription documents.</p>
      </div>
    );
  }

  return (
    <div className="rounded-sm border border-slate-200 bg-white p-5 sticky top-6 shadow-sm" data-testid="public-deal-response-card">
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Your Response</div>
      <h3 className="mt-1 text-lg font-semibold text-slate-900">How interested are you?</h3>
      {responded && (
        <div className="mt-2 text-xs font-mono uppercase tracking-widest text-emerald-700">
          Last response: {invitation.response_status?.replace("_", " ")} · {new Date(invitation.responded_at).toLocaleString()}
        </div>
      )}

      {!mode || mode !== "committed" ? (
        <>
          <div className="mt-4 space-y-2">
            <ResponseBtn icon={CheckCircle2} label="I'm Interested" desc="Soft-circle, keep me in the loop"
              onClick={()=>submit("interested")} disabled={busy} testid="response-interested"
              color="emerald" />
            <ResponseBtn icon={HelpCircle} label="Need More Info" desc="Send the data room / setup a call"
              onClick={()=>submit("need_info")} disabled={busy} testid="response-need-info"
              color="amber" />
            <ResponseBtn icon={XCircle} label="Pass" desc="Not a fit for me right now"
              onClick={()=>submit("pass")} disabled={busy} testid="response-pass"
              color="rose" />
            <button onClick={()=>setMode("committed")} disabled={busy}
              data-testid="response-commit-start"
              className="w-full mt-3 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white px-4 py-3 rounded-sm text-sm font-mono uppercase tracking-widest inline-flex items-center justify-center gap-2">
              <DollarSign className="w-4 h-4" /> Commit Capital
            </button>
          </div>
          <textarea rows={2} value={note} onChange={(e)=>setNote(e.target.value)}
            placeholder="Optional note to sponsor…"
            className="mt-3 w-full rounded-sm border border-slate-200 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-slate-300" />
        </>
      ) : (
        <div className="mt-4 space-y-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Commit Amount (USD)</div>
            <input type="number" inputMode="decimal" value={amount}
              onChange={(e)=>setAmount(e.target.value)} placeholder={deal.minimum_investment ? `Min ${fmtUSD(deal.minimum_investment)}` : "$0"}
              data-testid="commit-amount-input"
              className="w-full rounded-sm border border-slate-300 px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-slate-300" />
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">E-Signature (Type Full Legal Name)</div>
            <input value={esigName} onChange={(e)=>setEsigName(e.target.value)}
              placeholder="Pat Investor"
              data-testid="commit-esig-input"
              className="w-full rounded-sm border border-slate-300 px-3 py-2 font-mono italic focus:outline-none focus:ring-2 focus:ring-slate-300" />
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Optional Note</div>
            <textarea rows={2} value={note} onChange={(e)=>setNote(e.target.value)}
              className="w-full rounded-sm border border-slate-200 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-slate-300" />
          </div>
          <p className="text-[11px] text-slate-500 leading-snug">
            By submitting you acknowledge this is a non-binding indication of interest. The sponsor will follow up with formal subscription documents.
          </p>
          <div className="flex items-center gap-2">
            <button onClick={()=>setMode(null)} disabled={busy}
              className="flex-1 px-4 py-2 text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-slate-800">
              Cancel
            </button>
            <button onClick={()=>submit("committed")} disabled={busy}
              data-testid="commit-submit-btn"
              className="flex-[2] bg-indigo-700 hover:bg-indigo-800 disabled:opacity-50 text-white px-4 py-2.5 rounded-sm text-sm font-mono uppercase tracking-widest">
              {busy ? "Signing…" : "Sign & Commit"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ResponseBtn({ icon: Icon, label, desc, onClick, disabled, testid, color }) {
  const colorMap = {
    emerald: "border-emerald-200 hover:bg-emerald-50 text-emerald-700",
    amber:   "border-amber-200 hover:bg-amber-50 text-amber-700",
    rose:    "border-rose-200 hover:bg-rose-50 text-rose-700",
  };
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className={`w-full flex items-start gap-3 text-left rounded-sm border ${colorMap[color]} bg-white px-3 py-2.5 transition-colors disabled:opacity-50`}>
      <Icon className="w-5 h-5 shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="text-sm font-semibold">{label}</div>
        <div className="text-xs text-slate-500">{desc}</div>
      </div>
    </button>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <section className="rounded-sm border border-slate-200 bg-white p-5">
      <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" />} {title}
      </h3>
      {children}
    </section>
  );
}

function DLRow({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{label}</dt>
      <dd className="text-slate-800 font-mono font-semibold">{value}</dd>
    </div>
  );
}
